import { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { motion } from 'framer-motion';
import PageHero from '@/components/shared/PageHero';
import { publicApi } from '@/lib/api';
import { getIcon } from '@/lib/icons';
import { CheckCircle2, BarChart3 } from 'lucide-react';
import useSEO from '@/hooks/useSEO';

export default function ServicesPage() {
    const [services, setServices] = useState([]);
    const [yearStats, setYearStats] = useState([]);

    useSEO({
        title: 'Our Services',
        description:
            'VVVSI Trust services: Employment support (Daily Placements, Job Facilitation Center, sector-wise job fairs) with year-wise placement statistics, Career Guidance & Skill Development, Education Support, Health Camps, Disaster Relief and Environmental Initiatives.',
    });

    useEffect(() => {
        publicApi.getServices().then(setServices).catch(() => {});
        publicApi.getServiceYearStats().then(setYearStats).catch(() => {});
    }, []);

    return (
        <div data-testid="services-page">
            <PageHero
                eyebrow="Services"
                title="Comprehensive programs serving Tamil Nadu & Puducherry."
                subtitle="From job placement and career guidance to health, education, environment and disaster relief — delivered by a 3,400+ volunteer network."
            />
            <section className="section-pad">
                <div className="container-page space-y-16">
                    {services.map((s, idx) => {
                        const Icon = getIcon(s.icon);
                        const isEmployment = /employment/i.test(s.title);
                        return (
                            <motion.div
                                key={s.id}
                                initial={{ opacity: 0, y: 12 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5 }}
                                viewport={{ once: true, margin: '-80px' }}
                                data-testid={`service-block-${(s.title || '').toLowerCase().replace(/[^a-z]/g, '')}`}
                            >
                                <div
                                    className={`grid lg:grid-cols-12 gap-10 items-center ${
                                        idx % 2 === 1 ? 'lg:[&>:first-child]:order-2' : ''
                                    }`}
                                >
                                    <div className="lg:col-span-6 aspect-[16/10] rounded-2xl overflow-hidden bg-muted">
                                        <img
                                            src={s.image_url}
                                            alt={s.title}
                                            loading="lazy"
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                    <div className="lg:col-span-6">
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className="h-11 w-11 rounded-xl bg-[hsl(var(--primary))]/8 text-[hsl(var(--primary))] flex items-center justify-center">
                                                <Icon className="h-5 w-5" />
                                            </div>
                                            <Badge variant="outline" className="text-[10px]">
                                                Service {String(idx + 1).padStart(2, '0')}
                                            </Badge>
                                        </div>
                                        <h2 className="font-display text-3xl font-semibold tracking-tight mb-3">
                                            {s.title}
                                        </h2>
                                        <p className="text-foreground/80 leading-relaxed">
                                            {s.description || s.short_description}
                                        </p>
                                        {s.highlights?.length > 0 && (
                                            <ul className="mt-5 grid sm:grid-cols-2 gap-2">
                                                {s.highlights.map((h) => (
                                                    <li
                                                        key={h}
                                                        className="flex items-center gap-2 text-sm text-foreground/85"
                                                    >
                                                        <CheckCircle2 className="h-4 w-4 text-[hsl(var(--accent))]" />
                                                        {h}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>

                                {/* Year-wise placement statistics (only for Employment Services) */}
                                {isEmployment && yearStats.length > 0 && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 12 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.5, delay: 0.1 }}
                                        viewport={{ once: true, margin: '-40px' }}
                                        className="mt-10"
                                        data-testid="employment-year-stats"
                                    >
                                        <Card className="bg-card border-border overflow-hidden">
                                            <CardContent className="p-0">
                                                <div className="flex items-center gap-3 p-5 border-b border-border bg-[hsl(var(--secondary))]/40">
                                                    <div className="h-10 w-10 rounded-lg bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] flex items-center justify-center">
                                                        <BarChart3 className="h-5 w-5" />
                                                    </div>
                                                    <div>
                                                        <div className="text-xs font-semibold uppercase tracking-widest text-[hsl(var(--accent))]">
                                                            Employment Services
                                                        </div>
                                                        <div className="font-display text-lg font-semibold">
                                                            Year-wise Placement Statistics
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="overflow-x-auto">
                                                    <Table>
                                                        <TableHeader>
                                                            <TableRow>
                                                                <TableHead>Year</TableHead>
                                                                <TableHead className="text-right">
                                                                    Job Vacancies Posted
                                                                </TableHead>
                                                                <TableHead className="text-right">
                                                                    Candidates Placed
                                                                </TableHead>
                                                                <TableHead>Notes</TableHead>
                                                            </TableRow>
                                                        </TableHeader>
                                                        <TableBody>
                                                            {yearStats.map((row) => (
                                                                <TableRow
                                                                    key={row.id}
                                                                    data-testid={`year-stats-row-${row.year.replace(/\s+/g, '-')}`}
                                                                >
                                                                    <TableCell className="font-medium">
                                                                        {row.year}
                                                                    </TableCell>
                                                                    <TableCell className="text-right font-display text-[hsl(var(--primary))] font-semibold tabular-nums">
                                                                        {row.job_vacancies_posted || '—'}
                                                                    </TableCell>
                                                                    <TableCell className="text-right font-display text-[hsl(var(--primary))] font-semibold tabular-nums">
                                                                        {row.candidates_placed || '—'}
                                                                    </TableCell>
                                                                    <TableCell className="text-xs text-muted-foreground">
                                                                        {row.notes || ''}
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </motion.div>
                                )}
                            </motion.div>
                        );
                    })}
                </div>
            </section>
        </div>
    );
}
